/**
 * Composant de vérification d'adhésion FTPE
 * Bloque l'accès aux espaces COOPER'S et Expert si pas d'adhésion FTPE active
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Shield, CheckCircle, ArrowRight,
  Building, RefreshCw, Lock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getToken } from '../services/tokenService';
import { trackEvent } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ⚠️ Module Registre FTPE / PASS adhérent SUPPRIMÉ (26/05/2026).
// Seule la voie « Adhérer à la FTPE Outremer » (paiement) est conservée.
// L'ancienne modale NumeroAdherentDialog a été retirée définitivement.

// Composant pour afficher le blocage FTPE — V35.6 design premium split-screen
function FTPEAccessDenied({ onRetry }) {
  const navigate = useNavigate();

  const benefits = [
    { icon: Building, label: 'Accès aux dispositifs Garantie RCDS BATICO' },
    { icon: Shield, label: 'Représentation patronale officielle' },
    { icon: CheckCircle, label: 'Accompagnement personnalisé' },
    { icon: ArrowRight, label: 'Réseau de professionnels ultramarins' },
    { icon: CheckCircle, label: 'Services mutualisés FTPE' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 relative overflow-hidden">
      {/* Background décoratif */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -right-32 w-[28rem] h-[28rem] bg-indigo-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 left-1/2 w-[32rem] h-[32rem] bg-purple-500/10 rounded-full blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <div className="relative min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-6xl grid lg:grid-cols-[1.05fr_1fr] gap-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-blue-900/40 ring-1 ring-white/20 overflow-hidden">
          {/* === LEFT : Visuel + storytelling === */}
          <aside className="relative bg-gradient-to-br from-[#0B3C5D] via-[#0a1e3f] to-[#091a36] p-8 sm:p-10 lg:p-12 text-white flex flex-col justify-between min-h-[640px]">
            {/* Décor */}
            <div className="absolute inset-0 pointer-events-none opacity-30" aria-hidden="true">
              <div className="absolute top-10 right-10 w-32 h-32 border border-amber-300/30 rounded-full" />
              <div className="absolute top-20 right-20 w-20 h-20 border border-amber-300/20 rounded-full" />
              <div className="absolute bottom-32 left-10 w-40 h-40 border-2 border-blue-400/20 rounded-2xl rotate-12" />
            </div>

            <div className="relative">
              {/* Badge FTPE */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-300/30 text-amber-200 text-xs font-bold uppercase tracking-wider mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
                Espace réservé aux adhérents
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
                Rejoignez la <span className="text-amber-300">Fédération</span> des TPE Outremer
              </h1>
              <p className="text-blue-100/90 text-base sm:text-lg leading-relaxed mb-6 max-w-md">
                La FTPE représente et défend les intérêts des Très Petites Entreprises
                ultramarines. Une adhésion active débloque l&apos;accès à l&apos;ensemble des
                dispositifs solidaires et garanties COPPAM.
              </p>

              {/* Cotisation card */}
              <div className="inline-flex items-center gap-4 px-5 py-4 rounded-2xl bg-gradient-to-br from-amber-400/15 to-amber-500/5 border border-amber-300/40 backdrop-blur-sm">
                <div className="text-amber-300/80 text-xs uppercase tracking-wider font-semibold">
                  Cotisation
                  <br />
                  annuelle
                </div>
                <div className="border-l border-amber-300/30 pl-4">
                  <div className="text-4xl sm:text-5xl font-bold text-white leading-none">
                    200<span className="text-amber-300 text-2xl">€</span>
                  </div>
                  <div className="text-amber-200/80 text-xs mt-1">renouvelable chaque année</div>
                </div>
              </div>
            </div>

            {/* Trust signals bottom */}
            <div className="relative mt-10 pt-8 border-t border-white/10">
              <p className="text-xs uppercase tracking-wider text-blue-200/60 font-semibold mb-3">
                Ils nous font confiance
              </p>
              <div className="flex items-center gap-6 text-blue-100/70 text-sm">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-emerald-300" />
                  <span>RGPD compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-300" />
                  <span>SSL sécurisé</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-emerald-300" />
                  <span>Données chiffrées</span>
                </div>
              </div>
            </div>
          </aside>

          {/* === RIGHT : Actions + bénéfices === */}
          <section className="p-8 sm:p-10 lg:p-12 flex flex-col">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                <Lock className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              </div>
              <span className="text-xs uppercase tracking-widest font-bold text-blue-600 dark:text-blue-300">
                Accès protégé
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
              Adhésion FTPE requise
            </h2>
            <p className="text-slate-600 dark:text-slate-300 text-sm mb-7 leading-relaxed">
              Choisissez la voie d&apos;activation qui correspond à votre situation.
              L&apos;accès est instantané pour les paiements par carte bancaire et les
              adhérents existants.
            </p>

            {/* Bénéfices compacts */}
            <div className="mb-7">
              <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                Ce que vous débloquez
              </p>
              <ul className="space-y-2">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-200">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 flex items-center justify-center mt-0.5">
                      <CheckCircle className="w-3.5 h-3.5" />
                    </span>
                    <span>{b.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Actions */}
            <div className="space-y-3 mt-auto">
              <Button
                onClick={() => {
                  trackEvent('cta.ftpe_adherer', { from: 'access_denied_main' });
                  navigate('/adhesion-ftpe-workflow');
                }}
                className="w-full h-14 text-base font-semibold bg-gradient-to-r from-[#0B3C5D] to-[#0a1e3f] hover:from-[#0a3252] hover:to-[#091a36] shadow-lg shadow-blue-900/30 hover:shadow-xl hover:shadow-blue-900/40 transition-all hover:scale-[1.01] group"
                data-testid="ftpe-adherer-btn"
              >
                <Building className="w-5 h-5 mr-2.5" />
                Adhérer à la FTPE Outremer
                <ArrowRight className="w-5 h-5 ml-2.5 group-hover:translate-x-0.5 transition-transform" />
              </Button>

              <div className="grid grid-cols-2 gap-2 pt-2">
                <Button
                  variant="ghost"
                  className="text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  onClick={onRetry}
                  data-testid="ftpe-retry-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Vérifier à nouveau
                </Button>
                <Button
                  variant="ghost"
                  className="text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  onClick={() => navigate('/')}
                >
                  Retour à l&apos;accueil
                </Button>
              </div>
            </div>

            {/* Contact */}
            <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700 text-center">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Une question ?{' '}
                <a
                  href="mailto:contact@ftpe-outremer.fr"
                  className="text-blue-600 dark:text-blue-400 font-medium hover:underline"
                >
                  contact@ftpe-outremer.fr
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

// Composant pour afficher l'adhésion en attente
function FTPEPendingAccess({ status, onRetry }) {
  const navigate = useNavigate();
  
  const statusMessages = {
    pending: {
      title: 'Adhésion en attente de paiement',
      description: 'Votre demande d\'adhésion a été créée. Complétez le paiement pour activer votre accès.',
      action: 'Finaliser le paiement'
    },
    signed: {
      title: 'Adhésion signée - Paiement requis',
      description: 'Vous avez signé votre bulletin d\'adhésion. Procédez au paiement pour activer votre compte.',
      action: 'Payer maintenant'
    },
    draft: {
      title: 'Adhésion en cours',
      description: 'Votre demande d\'adhésion est en cours. Veuillez la compléter pour accéder à cet espace.',
      action: 'Continuer l\'adhésion'
    }
  };
  
  const config = statusMessages[status] || statusMessages.pending;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-lg w-full shadow-xl border-blue-200">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-10 h-10 text-blue-600" />
          </div>
          <Badge className="mx-auto mb-2 bg-blue-700 text-white font-semibold">
            Statut: {status?.toUpperCase()}
          </Badge>
          <CardTitle className="text-2xl text-gray-900">
            {config.title}
          </CardTitle>
          <CardDescription className="text-base">
            {config.description}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <Button 
            onClick={() => navigate('/adhesion-ftpe-workflow')}
            className="w-full bg-blue-500 hover:bg-blue-600 h-12 text-base"
          >
            {config.action}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onRetry}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => navigate('/')}>
              Retour
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Hook pour vérifier l'accès FTPE
export function useFTPEAccess() {
  const { user, getAuthHeader: getAuthHeader, isAuthenticated } = useAuth();
  const [ftpeStatus, setFtpeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, _setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const checkAccess = async (userId = null) => {
    // Essayer d'obtenir l'ID utilisateur de plusieurs sources
    const effectiveUserId = userId || user?.id;
    const storedToken = getToken();
    
    // Si pas de token, pas authentifié
    if (!storedToken) {
      setLoading(false);
      return;
    }
    
    // Si pas d'ID utilisateur, essayer de le récupérer depuis le token ou attendre
    if (!effectiveUserId) {
      if (retryCount < 5) {
        setRetryCount(prev => prev + 1);
        return; // Le useEffect va re-déclencher avec le nouveau retryCount
      }
      setLoading(false);
      return;
    }
    
    // Les Super Admins et Admins ont toujours accès
    const effectiveRole = user?.role;
    if (effectiveRole === 'super_admin' || effectiveRole === 'admin') {
      setFtpeStatus({ hasAccess: true, status: 'admin_bypass' });
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${storedToken}` };
      
      
      const res = await axios.get(
        `${BACKEND_URL}/api/ftpe-adhesion/admin/check-member-ftpe/${effectiveUserId}`,
        { headers }
      );
      
      
      // Vérifier si l'adhésion est active (plusieurs statuts possibles)
      const activeStatuses = ['active', 'completed', 'paid', 'signed'];
      const hasActiveAccess = res.data.has_ftpe && 
        activeStatuses.includes(res.data.status);
      
      
      setFtpeStatus({
        hasAccess: hasActiveAccess,
        hasFtpe: res.data.has_ftpe,
        status: res.data.status,
        adhesionId: res.data.adhesion_id,
        expirationDate: res.data.expiration_date
      });
    } catch {
      // Si erreur 403 ou 404, pas d'adhésion
      setFtpeStatus({ hasAccess: false, status: null });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      checkAccess();
    }, 200 + (retryCount * 300)); // Délai croissant à chaque retry
    return () => clearTimeout(timer);
  }, [user?.id, isAuthenticated, retryCount]);

  return { ftpeStatus, loading, checkAccess, error };
}

// HOC pour protéger les pages nécessitant FTPE
export function withFTPEAccess(WrappedComponent) {
  return function FTPEProtectedComponent(props) {
    const { ftpeStatus, loading, checkAccess } = useFTPEAccess();
    
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-gray-600">Vérification de votre adhésion FTPE...</p>
          </div>
        </div>
      );
    }
    
    // Accès autorisé
    if (ftpeStatus?.hasAccess) {
      return <WrappedComponent {...props} ftpeStatus={ftpeStatus} />;
    }
    
    // Adhésion en cours mais pas active
    if (ftpeStatus?.hasFtpe && !ftpeStatus?.hasAccess) {
      return <FTPEPendingAccess status={ftpeStatus.status} onRetry={checkAccess} />;
    }
    
    // Pas d'adhésion
    return <FTPEAccessDenied onRetry={checkAccess} />;
  };
}

export default withFTPEAccess;
